import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Box, Button, Slider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

// Helper to get the cropped image as a data URL
function getCroppedImg(imageSrc, croppedAreaPixels) {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );
      
      resolve(canvas.toDataURL('image/png'));
    };
  });
}

export default function ImageCropper({ element, updateElement, ElementControls, isInZone = false, layoutId = null, zoneId = null, onElementClick = null }) {
  const [imageSrc, setImageSrc] = useState(element?.imageSrc || '');
  const [crop, setCrop] = useState(element?.crop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(element?.zoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(element?.croppedAreaPixels || null);
  const [croppedImage, setCroppedImage] = useState(element?.croppedImage || '');
  const [editing, setEditing] = useState(!element?.croppedImage);
  

  
  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const isResizingRef = useRef(false);
  const resizeStartDataRef = useRef(null);
  const resizeDirectionRef = useRef(null);
  const containerRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const currentWidth = element?.styles?.width || 300;
  const currentHeight = element?.styles?.height || 200;
  const aspectRatio = useMemo(() => {
    return currentHeight > 0 ? currentWidth / currentHeight : 1.5; // Prevent division by zero
  }, [currentWidth, currentHeight]);
  const maintainAspectRatio = true; // Always maintain aspect ratio



  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);



  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (imageSrc && croppedAreaPixels) {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setEditing(false);
      
      // Update the element with the cropped image data and size
      updateElement(element.id, {
        imageSrc,
        croppedImage: cropped,
        crop,
        zoom,
        croppedAreaPixels,
        styles: {
          ...element.styles,
          width: currentWidth,
          height: currentHeight
        }
      }, isInZone, layoutId, zoneId);
    }
  }, [imageSrc, croppedAreaPixels, crop, zoom, element?.id, updateElement, isInZone, layoutId, zoneId, currentWidth, currentHeight, element?.styles]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (imageSrc && croppedAreaPixels && editing) {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setEditing(false);
      
      // Update the element with the cropped image data and size
      updateElement(element.id, {
        imageSrc,
        croppedImage: cropped,
        crop,
        zoom,
        croppedAreaPixels,
        styles: {
          ...element.styles,
          width: currentWidth,
          height: currentHeight
        }
      }, isInZone, layoutId, zoneId);
    }
  }, [imageSrc, croppedAreaPixels, crop, zoom, element?.id, updateElement, isInZone, layoutId, zoneId, currentWidth, currentHeight, element?.styles, editing]);

  const handleEdit = () => setEditing(true);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newImageSrc = ev.target.result;
        setImageSrc(newImageSrc);
        setEditing(true);
        setCroppedImage('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        
        // Update the element with the new image source
        updateElement(element.id, {
          imageSrc: newImageSrc,
          croppedImage: '',
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null
        }, isInZone, layoutId, zoneId);
      };
      reader.readAsDataURL(file);
    }
  };

  // Resize handlers
  const handleResizeMove = useCallback((e) => {
    if (!isResizingRef.current || !resizeStartDataRef.current) {
      return;
    }
    
    e.preventDefault();
    const deltaX = e.clientX - resizeStartDataRef.current.startX;
    const deltaY = e.clientY - resizeStartDataRef.current.startY;
    
    let newWidth = resizeStartDataRef.current.startWidth;
    let newHeight = resizeStartDataRef.current.startHeight;
    
    // Calculate new size based on resize direction
    switch (resizeDirectionRef.current) {
      case 'se':
        newWidth = Math.max(100, Math.min(800, resizeStartDataRef.current.startWidth + deltaX));
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.max(100, Math.min(600, resizeStartDataRef.current.startHeight + deltaY));
        }
        break;
      case 'sw':
        newWidth = Math.max(100, Math.min(800, resizeStartDataRef.current.startWidth - deltaX));
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.max(100, Math.min(600, resizeStartDataRef.current.startHeight + deltaY));
        }
        break;
      case 'ne':
        newWidth = Math.max(100, Math.min(800, resizeStartDataRef.current.startWidth + deltaX));
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.max(100, Math.min(600, resizeStartDataRef.current.startHeight - deltaY));
        }
        break;
      case 'nw':
        newWidth = Math.max(100, Math.min(800, resizeStartDataRef.current.startWidth - deltaX));
        if (maintainAspectRatio) {
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.max(100, Math.min(600, resizeStartDataRef.current.startHeight - deltaY));
        }
        break;
      default:
        break;
    }
    
    // Only update if dimensions actually changed and not currently editing
    const currentWidth = element?.styles?.width || 300;
    const currentHeight = element?.styles?.height || 200;
    
    if (!editing && (Math.abs(newWidth - currentWidth) > 1 || Math.abs(newHeight - currentHeight) > 1)) {
      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Debounce the update to prevent too frequent updates
      resizeTimeoutRef.current = setTimeout(() => {
        updateElement(element.id, {
          styles: {
            ...element.styles,
            width: newWidth,
            height: newHeight
          }
        }, isInZone, layoutId, zoneId);
      }, 16); // ~60fps
    }
  }, [element?.styles, element?.id, updateElement, isInZone, layoutId, zoneId, maintainAspectRatio, aspectRatio, editing]);

  const handleResizeEnd = useCallback(async () => {
    // Clear any pending resize timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }
    
    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);
    isResizingRef.current = false;
    resizeDirectionRef.current = null;
    resizeStartDataRef.current = null;
    
    // Auto-save if editing
    if (editing && imageSrc && croppedAreaPixels) {
      await autoSave();
    }
    
    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, editing, imageSrc, croppedAreaPixels, autoSave]);

  const handleResizeStart = useCallback((e, direction) => {
    // Don't allow resize during editing
    if (editing) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    isResizingRef.current = true;
    resizeDirectionRef.current = direction;
    
    const startX = e.clientX;
    const startY = e.clientY;
    
    // Parse current dimensions to ensure they're numbers
    let startWidth = currentWidth;
    let startHeight = currentHeight;
    if (typeof startWidth === 'string') {
      startWidth = parseInt(startWidth.replace('px', ''));
    }
    if (typeof startHeight === 'string') {
      startHeight = parseInt(startHeight.replace('px', ''));
    }
    if (isNaN(startWidth)) startWidth = 300;
    if (isNaN(startHeight)) startHeight = 200;
    
    const resizeData = {
      startX,
      startY,
      startWidth,
      startHeight,
      elementId: element.id
    };
    
    setResizeStartData(resizeData);
    resizeStartDataRef.current = resizeData;
    
    // Add global event listeners
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [currentWidth, currentHeight, element.id, handleResizeMove, handleResizeEnd, editing]);

  return (
    <Box 
      sx={{
        width: currentWidth,
        display: 'inline-block',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        cursor: 'pointer',
        verticalAlign: 'top'
      }}
      onClick={async (e) => {
        e.stopPropagation();
        
        // Auto-save if we're currently editing
        if (editing && imageSrc && croppedAreaPixels) {
          await autoSave();
        }
        
        if (onElementClick) {
          onElementClick(element, isInZone, layoutId, zoneId);
        }
      }}
    >
      <ElementControls />

      <Box 
        ref={containerRef}
        sx={{
          width: currentWidth,
          height: currentHeight,
          backgroundColor: '#e0e0e0',
          position: 'relative',
          overflow: 'visible',
          mb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isResizing ? 'nw-resize' : 'default'
        }}
      >
        {croppedImage && !editing ? (
          <>
            <img
              src={croppedImage}
              alt="Cropped"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                borderRadius: element.styles?.borderRadius || '0px'
              }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleEdit}
              sx={{ 
                position: 'absolute', 
                bottom: -60, 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 2
              }}
            >
              Crop
            </Button>
          </>
        ) : imageSrc ? (
          <>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </>
        ) : (
          <label style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <span style={{ color: '#888', fontSize: '12px' }}>Drag or click to upload</span>
          </label>
        )}

        {/* Resize handles - only show when not editing */}
        {imageSrc && !editing && (
          <>
            {/* Top-left handle */}
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                left: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'nw-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'nw')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Top-right handle */}
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'ne-resize',
                zIndex: '20',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'ne')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Bottom-left handle */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                left: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'sw-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'sw')}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Bottom-right handle */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                right: -6,
                width: 16,
                height: 16,
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                cursor: 'se-resize',
                zIndex: 20,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'scale(1.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }
              }}
              onMouseDown={(e) => handleResizeStart(e, 'se')}
              onClick={(e) => e.stopPropagation()}
            />
          </>
        )}
      </Box>
      
      {imageSrc && editing && (
        <Box sx={{ width: 300, mt: 1 }}>
            <Slider
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(_, value) => setZoom(value)}
              aria-labelledby="Zoom"
            />
          </Box>
      )}
    </Box>
  );
} 